// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { resolveFieldLabel, resolveLocalizedValue } from "@/features/tasks/action-tool-labels";
import { formatFieldValue } from "@/features/tasks/sample-task";
import type { FieldColor, FieldType, FieldVariant } from "@/features/tasks/types";
import { cn } from "@/lib/utils";

/**
 * The label/value chip the Action Tool renders for every configured field.
 *
 * Measurements come from `TaskCardContentBlocksField`: a 9px outlined label followed by the
 * formatted value at 12px. `variant: "outlined"` tints the label's border with the field colour;
 * `"standard"` leaves it white against the card. The label text itself stays neutral in both —
 * only the border carries the colour.
 */

/** Neutral ink, from the Action Tool theme's `neutral.neutral20`. */
const INK = "#27272E";

/** Field colours resolve against the Action Tool's MUI palette, not the back-office theme. */
const FIELD_COLOR_HEX: Record<FieldColor, string> = {
  info: INK,
  error: "#d32f2f",
  warning: "#ed6c02",
  success: "#2e7d32",
  primary: "#1976d2",
  secondary: "#9c27b0",
};

/** A field's colour is either a palette name or a raw hex, as stored in the appearance JSON. */
function resolveColor(color: FieldColor | string | undefined): string {
  if (!color) return FIELD_COLOR_HEX.info;
  if (color.startsWith("#")) return color;
  return FIELD_COLOR_HEX[color as FieldColor] ?? FIELD_COLOR_HEX.info;
}

interface TaskCardChipProps {
  /** The field's configured `label` — an Action Tool i18n key, not display text. */
  label: string;
  value: unknown;
  type?: FieldType;
  color?: FieldColor | string;
  variant?: FieldVariant | string;
  /** Chain the preview is scoped to, so per-chain label overrides apply. */
  chainName?: string;
  className?: string;
}

export function TaskCardChip({
  label,
  value,
  type = "text",
  color,
  variant = "standard",
  chainName,
  className,
}: TaskCardChipProps) {
  const accent = resolveColor(color);
  // `localizedText` resolves its value through the Action Tool's table; every other type formats
  const formatted =
    type === "localizedText" && typeof value === "string"
      ? resolveLocalizedValue(value)
      : formatFieldValue(type, value);

  return (
    <div className={cn("min-w-0 leading-tight", className)}>
      <span
        className="mr-2 inline-block rounded-[2px] border px-1 py-0.5 text-[9px] font-normal leading-3 tracking-[0.5px]"
        style={{
          borderColor: variant === "standard" ? "#ffffff" : accent,
          backgroundColor: "#ffffff",
          color: INK,
        }}
      >
        {resolveFieldLabel(label, chainName)}
      </span>
      <span className="text-[12px] font-medium leading-4 tracking-[0.5px]" style={{ color: INK }}>
        {formatted}
      </span>
    </div>
  );
}
